import { invoke } from '@tauri-apps/api/core'
import { useSyncExternalStore } from 'react'

/**
 * O que o web app precisa saber sobre estar dentro do aplicativo instalado.
 *
 * Nothing here is imported conditionally: `@tauri-apps/api` is a few hundred
 * bytes of message passing and importing it in a browser is harmless. What is
 * NOT harmless is calling `invoke` there, so every call goes through
 * `isDesktop()` first.
 */

/**
 * Whether this page is running inside the Tauri shell.
 *
 * Checked on the injected internals rather than the user agent: the WebView
 * reports itself as Chrome or WebKit, which is true and useless.
 */
export const isDesktop = (): boolean =>
  typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window

/**
 * Whether this window is one module rather than the whole application.
 *
 * A janela NATIVA da F004, aberta com `?window=module`. Desde a F138 ninguém a
 * abre — o Modo PC desenha as janelas dentro do Aeris (ADR-0037) —, e ela fica
 * reservada para um futuro "destacar para outro monitor".
 */
export const isModuleWindow = (): boolean =>
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('window') === 'module'

// ---------------------------------------------------------------------------
// Modo PC — F138
// ---------------------------------------------------------------------------

const PC_MODE_KEY = 'aeris.pc-mode'
const PREVIEW_KEY = 'aeris.desktop-preview'
const PC_MODE_EVENT = 'aeris:pc-mode'

/**
 * A prévia do Modo PC num navegador comum — só em desenvolvimento.
 *
 * Existe para o Playwright: as janelas internas são páginas como qualquer
 * outra, e testá-las num Chromium é o que dá para automatizar. `DEV` é
 * substituído por `false` no build, então o instalador e o site nunca a
 * oferecem, com ou sem a chave guardada.
 */
export const isDesktopPreview = (): boolean =>
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  window.localStorage.getItem(PREVIEW_KEY) === 'true'

/**
 * Onde o Modo PC pode ser ligado: no aplicativo instalado.
 *
 * Durante as fatias 1 a 4 da F138 havia também um portão de build
 * (`VITE_DESKTOP_SHELL`), para o instalador da loja não receber metade de uma
 * área de trabalho. A fatia 5 entregou a última parte e tirou o portão.
 */
export const desktopShellAvailable = (): boolean =>
  isDesktopPreview() || isDesktop()

/**
 * Modo PC: a área de trabalho com janelas, em vez da barra lateral.
 *
 * Por aparelho, e não por usuário: é uma propriedade da máquina — o PC do
 * balcão e o notebook do escritório querem respostas diferentes, e a mesma
 * pessoa usa os dois.
 *
 * **Ligado quando ninguém escolheu** (decisão do dono, F138): quem instalou o
 * Aeris no computador da loja quer o Aeris de computador. Quem desligou
 * continua desligado.
 */
export const isPcMode = (): boolean => {
  if (!desktopShellAvailable()) return false
  const stored = window.localStorage.getItem(PC_MODE_KEY)
  return stored === null || stored === 'true'
}

export const setPcMode = (enabled: boolean): void => {
  window.localStorage.setItem(PC_MODE_KEY, String(enabled))
  // O `storage` só avisa as OUTRAS páginas; esta precisa do próprio aviso para
  // trocar de moldura na hora.
  window.dispatchEvent(new Event(PC_MODE_EVENT))
}

const subscribeToPcMode = (listener: () => void): (() => void) => {
  window.addEventListener(PC_MODE_EVENT, listener)
  window.addEventListener('storage', listener)
  return () => {
    window.removeEventListener(PC_MODE_EVENT, listener)
    window.removeEventListener('storage', listener)
  }
}

/** O Modo PC, e a moldura muda assim que alguém liga ou desliga. */
export const usePcMode = (): boolean =>
  useSyncExternalStore(subscribeToPcMode, isPcMode, () => false)

// ---------------------------------------------------------------------------
// Janelas nativas (F004) — sem quem as chame desde a F138
// ---------------------------------------------------------------------------

/**
 * Opens a module in its own native window, or raises the one already open.
 *
 * Returns false when it could not — which is the caller's signal to navigate
 * instead. Kept, with the Rust commands behind it, for "destacar para outro
 * monitor" (ADR-0037).
 */
export const openModuleWindow = async (input: {
  module: string
  path: string
  title: string
}): Promise<boolean> => {
  if (!isDesktop()) return false

  try {
    await invoke('open_module_window', input)
    return true
  } catch {
    return false
  }
}

export const closeModuleWindow = async (module: string): Promise<void> => {
  if (!isDesktop()) return
  try {
    await invoke('close_module_window', { module })
  } catch {
    // The window was already gone, which is the outcome that was asked for.
  }
}

/** Which modules currently have a window, asked of the shell rather than remembered. */
export const openModuleWindows = async (): Promise<readonly string[]> => {
  if (!isDesktop()) return []
  try {
    return await invoke<string[]>('open_module_windows')
  } catch {
    return []
  }
}
